import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useReturns } from '@/hooks/useReturns';
import ReturnModal from './components/ReturnModal';
import { updateReturnStatus } from '@/services/returns';
import { useAuth } from '@/context/AuthContext';
import { isOperationsRole } from '@/lib/rbac';

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pendiente', color: 'text-amber-600', bg: 'bg-amber-50' },
  received: { label: 'Recibido', color: 'text-cyan-600', bg: 'bg-cyan-50' },
  refunded: { label: 'Reembolsado', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  closed: { label: 'Cerrado', color: 'text-foreground-600', bg: 'bg-background-200' },
  rejected: { label: 'Rechazado', color: 'text-red-600', bg: 'bg-red-50' },
};

const STATUSES = ['all', 'pending', 'received', 'refunded', 'closed', 'rejected'];
const STATUS_LABELS: Record<string, string> = {
  all: 'Todas',
  pending: 'Pendientes',
  received: 'Recibidas',
  refunded: 'Reembolsadas',
  closed: 'Cerradas',
  rejected: 'Rechazadas',
};

export default function ReturnsPage() {
  const { profile } = useAuth();
  const isOperations = isOperationsRole(profile?.role ?? '');
  const [filter, setFilter] = useState('all');
  const { data: returns, loading, error, refetch } = useReturns();
  const [showNew, setShowNew] = useState(false);
  const [actionError, setActionError] = useState('');

  async function handleStatusChange(id: string, status: string) {
    setActionError('');
    const { error: updateError } = await updateReturnStatus(id, status);
    if (updateError) setActionError(updateError);
    refetch();
  }

  const filtered = returns.filter((r) => filter === 'all' || r.status === filter);

  const counts: Record<string, number> = {};
  STATUSES.forEach((s) => {
    counts[s] = s === 'all' ? returns.length : returns.filter((r) => r.status === s).length;
  });

  const authorizedAmount = returns
    .filter((r) => r.status !== 'rejected')
    .reduce((sum, r) => sum + (r.refundAmount || 0), 0);
  const refundedAmount = returns
    .filter((r) => r.status === 'refunded')
    .reduce((sum, r) => sum + (r.refundAmount || 0), 0);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        {actionError && (
          <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{actionError}</span>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Devoluciones</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{returns.length} devoluciones registradas</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          {!isOperations && (
            <button
              onClick={() => setShowNew(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nueva Devolución
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total Devoluciones</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{returns.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Monto Autorizado</p>
            <p className="text-xl font-bold text-amber-600 mt-1">${authorizedAmount.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Reembolsado</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">${refundedAmount.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Pendientes</p>
            <p className="text-xl font-bold text-accent-600 mt-1">{counts.pending || 0}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                filter === s ? 'bg-accent-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {STATUS_LABELS[s]} ({counts[s] || 0})
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Devolución</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Cliente</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden xl:table-cell">Vendedor</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden md:table-cell">Venta original</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Motivo</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Monto autorizado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const st = STATUS_MAP[r.status] || { label: r.status, color: 'text-foreground-500', bg: 'bg-background-100' };
                  return (
                    <tr key={r.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3.5 text-accent-600 font-medium whitespace-nowrap">{r.returnNumber}</td>
                      <td className="px-5 py-3.5">
                        <div>
                          <p className="text-foreground-900 font-medium text-sm">{r.customerName || '—'}</p>
                          {r.customerCity && <p className="text-xs text-foreground-500">{r.customerCity}</p>}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden xl:table-cell text-foreground-700">{r.salespersonName || '—'}</td>
                      <td className="px-5 py-3.5 hidden md:table-cell text-foreground-700">{r.saleNumber || '—'}</td>
                      <td className="px-5 py-3.5">
                        <div>
                          <p className="text-foreground-700 text-sm max-w-[220px] truncate">{r.reason}</p>
                          <p className="text-xs text-foreground-400">
                            {r.items.length} ítem(s) · {r.returnType === 'full_return' ? 'Devolución total' : 'Devolución parcial'}
                          </p>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-right text-foreground-900 font-medium whitespace-nowrap">${(r.refundAmount || 0).toLocaleString()}</td>
                      <td className="px-5 py-3.5">
                        <select
                          value={r.status}
                          onChange={(e) => handleStatusChange(r.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${st.color} ${st.bg}`}
                        >
                          {STATUSES.filter((s) => s !== 'all').map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell text-xs text-foreground-500 whitespace-nowrap">
                        {r.returnDate ? new Date(r.returnDate).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-inbox-line text-3xl block mb-2"></i>
              <p className="text-sm">No se encontraron devoluciones</p>
            </div>
          )}
          {loading && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-2xl block mb-2"></i>
              <p className="text-sm">Cargando devoluciones...</p>
            </div>
          )}
        </div>
      </div>

      {showNew && <ReturnModal onClose={() => setShowNew(false)} onSaved={refetch} />}
    </AdminLayout>
  );
}